<template>
  <footer v-editable="blok" class="footer" :class="[isDark ? 'is-dark' : 'is-light']">
    <div class="footer-grid">
      <div class="footer-container">
        <!-- Richtext -->
        <div class="main-content">
          <div class="claim">
            <SharedBaseRichtext class="heading-main-large" :text="blok.statement" :isProseEnabled="false" />
          </div>

          <div v-if="blok.main_links?.length || blok.teaser_cta?.length" class="columns-container">
            <template v-if="blok.main_links?.length">
              <div
                v-if="blok.main_links[0].items_general?.length"
                v-editable="blok.main_links[0].items_general"
                class="column">
                <ul class="column-links">
                  <!-- General links -->
                  <li v-for="(item, j) in blok.main_links[0].items_general" :key="j" v-editable="item">
                    <UtilLinkItemIcon
                      v-if="item.component === 'util_link-overlay-item'"
                      :blok="item"
                      :isDark="false"
                      :hideInternalIcon="true"
                      :detectIcon="true" />

                    <UtilLinkItem
                      v-if="item.component === 'util_link-item'"
                      :blok="item"
                      :isDark="false"
                      :detectIcon="true" />
                  </li>
                </ul>
              </div>

              <div
                v-if="blok.main_links[0].items?.length || blok.main_links[0].items_social?.length"
                v-editable="blok.main_links[0]"
                class="column">
                <ul class="column-links">
                  <!-- Contact links -->
                  <li v-for="(item, j) in blok.main_links[0].items" :key="j" v-editable="item">
                    <UtilLinkItemIcon
                      v-if="item.component === 'util_link-overlay-item'"
                      :blok="item"
                      :isDark="false"
                      :detectIcon="true" />

                    <UtilLinkItem
                      v-if="item.component === 'util_link-item'"
                      :blok="item"
                      :isDark="false"
                      :detectIcon="true" />
                  </li>

                  <!-- Hidden text links -->
                  <li v-if="blok.main_links[0].items_social?.length">
                    <ul
                      class="column-links hidden-text-links"
                      :class="{ 'top-space': blok.main_links[0].items?.length }">
                      <li v-for="(item, j) in blok.main_links[0].items_social" :key="j" v-editable="item">
                        <UtilLinkItemIcon
                          v-if="item.component === 'util_link-overlay-item'"
                          forceIconPosition="start"
                          :blok="item"
                          :isDark="false"
                          :detectIcon="true" />

                        <UtilLinkItem
                          v-if="item.component === 'util_link-item'"
                          :blok="item"
                          :isDark="false"
                          :detectIcon="true" />
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </template>

            <div v-if="blok.teaser_text || blok.teaser_cta?.length" class="column column-newsletter">
              <SharedBaseRichtext
                class="action-medium-regular"
                :text="blok.teaser_text"
                isProseEnabled
                textSize="medium" />
              <UtilButton v-if="blok.teaser_cta?.length" v-editable="blok.teaser_cta[0]" :blok="blok.teaser_cta[0]" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Section bottom -->
    <div class="footer-meta">
      <WaveSvg class="footer-wave" />
      <div class="footer-grid">
        <div class="bottom-container">
          <!-- Logo -->
          <div class="logo-container">
            <LogoSvg />
          </div>

          <!-- Links -->
          <ul class="bottom-links">
            <li v-for="(item, index) in blok.legal_links" :key="index" v-editable="item">
              <SharedBaseLink :link="item.link">
                {{ item.text }}
              </SharedBaseLink>
            </li>

            <!-- Hardcoded Cookie-Consent -->
            <li v-editable="blok.legal_cookieconsent_text">
              <SharedBaseLink
                :link="cookieLink"
                @click="
                  () => {
                    consentState.visible = 'preferences';
                  }
                ">
                {{ blok.legal_cookieconsent_text }}
              </SharedBaseLink>
            </li>
          </ul>

          <p class="bottom-text">
            {{ blok.legal_copyright_text }}
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { SharedBaseLink, SharedBaseRichtext } from '@seb-platform/shared/components/base';
import { useConsent } from '@seb-platform/shared/composables';
import LogoSvg from '~/assets/svg/logo_onlight.svg?skipsvgo';
import WaveSvg from '~/assets/svg/schaerer_wave.svg?skipsvgo';
import UtilButton from '~/components/storyblok/utils/UtilButton/UtilButton.vue';
import UtilLinkItem from '~/components/storyblok/utils/UtilLinkItem/UtilLinkItem.vue';
import UtilLinkItemIcon from '~/components/storyblok/utils/UtilLinkItemIcon/UtilLinkItemIcon.vue';
import type { BmFooterStoryblok, MultilinkStoryblok } from '~/types/storyblok-generated';

const { state: consentState } = useConsent();

const props = defineProps<{
  blok: BmFooterStoryblok;
  isDark?: boolean;
}>();

/* tbd */
const cookieLink = computed(() => {
  return {
    _uid: '52cf2156-9097-4082-8279-22ca43f89ef9',
    link: {
      id: '',
      url: '#cookies',
      linktype: 'url',
      fieldtype: 'multilink',
      cached_url: '#cookies',
    },
    text: props.blok.legal_cookieconsent_text,
    component: 'util_link-item',
  } as unknown as MultilinkStoryblok;
});
</script>

<style src="./BMFooter.scss" lang="scss" scoped />
